// Phase — Reports Dashboard. Generic daily-trend area chart, reused for both
// Sales Trend and Customer Growth (same shape: [{ date, value }]).
import * as React from "react";
import { format } from "date-fns";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export interface TrendPoint {
  date: string;
  value: number;
}

export interface TrendAreaChartProps {
  data: TrendPoint[];
  valueFormatter?: (v: number) => string;
  // "day" (default) keeps the original daily-trend tick/tooltip format used
  // by Sales Trend / Customer Growth; "month" is for series bucketed by
  // month (e.g. Customer Credit / Vendor dashboards, which have no date picker).
  granularity?: "day" | "month";
}

export function TrendAreaChart({ data, valueFormatter, granularity = "day" }: TrendAreaChartProps) {
  const fmt = valueFormatter ?? ((v: number) => String(v));
  const tickFormat = granularity === "month" ? "MMM yyyy" : "dd MMM";
  const tooltipFormat = granularity === "month" ? "MMM yyyy" : "dd MMM yyyy";
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
        <XAxis
          dataKey="date"
          tickFormatter={(d) => format(new Date(d), tickFormat)}
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tickFormatter={(v) => fmt(v)}
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          axisLine={false}
          tickLine={false}
          width={56}
        />
        <Tooltip
          formatter={(v: number) => fmt(v)}
          labelFormatter={(d) => format(new Date(d), tooltipFormat)}
          contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
        />
        <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#trendFill)" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default TrendAreaChart;
